import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Content } from '../../components/common/CommonTypes';
import { getSuppressionPageDetails } from '../thunks/getSuppressionContent';

/**
 * Redux slice state for CMS/content payload returned by the backend.
 */
type SliceState = Content;

/**
 * Initial content state.
 */
const initialSiteState: SliceState = {
  productPage: {
    productTypeCustomisation: [],
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: '',
    noProductsFound: ''
  },
  productVariantPage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: '',
    disclaimer: '',
    paymentProfileLabel: '',
    manufacturersWarrantyDuration: 0,
    policyStartDateRule: 0,
    policyStartDateExceptionLimitDays: 0
  },
  landingPage: {
    backgroundImageUrl: '',
    backgroundImageAltText: '',
    title: '',
    description: '',
    information: '',
    warning: '',
    videoUrl: '',
    journeyTimeNote: '',
    useVinInput: false,
    forceVinCheck: false,
    vinNotFoundText: '',
    whySection: {
      whySectionHeader: '',
      whySectionDescription: '',
      whySectionLists: [
        {
          whySectionListHeader: '',
          caveatText: '',
          whySectionListItems: [
            {
              whySectionItemTitle: '',
              whySectionItemDescription: '',
              whySectionItemLogoUrl: ''
            }
          ]
        }
      ]
    },
    anyQuestionsSection: {
      landingPageQuestionsSectionHeader: '',
      landingPageQuestionsSectionDescription: '',
      landingPageQuestionsSectionNumber: '',
      landingPageQuestionsSectionEmail: '',
      openingHours: [
        {
          openingDays: '',
          openingTimes: '',
          availableServices: ''
        },
        {
          openingDays: '',
          openingTimes: '',
          availableServices: ''
        }
      ]
    },
    videoAlternativeImageUrl: '',
    videoAlternativeImageAltText: '',
    wistiaEmbedUrl: '',
    renewalPeriodExpiredMessage: '',
    multipleLoginAttemptsMessage: ''
  },
  vehiclePage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: '',
    upsellAnnualMileage: '',
    hideDealerReference: false,
    hidePurchasePrice: false,
    hideDeliveryDate: false,
    hideMileage: false,
    editablePurchasePrice: false,
    editableMileage: false,
    hideEstimatedDeliveryDate: false,
    mandatorySalesId: false,
    externalReferenceLabelText: '',
    hideDealerReferenceCCI: false,
    hideVinInput: false,
    mandatoryExternalReference: false,
    mandatoryExternalReferenceCCI: false,
    mandatoryVin: false,
    mandatoryVrn: false
  },
  customerPage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: '',
    isDateOfBirthOptional: false,
    phoneNumberLabel: ''
  },
  summaryPage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: '',
    acceptanceText: [],
    acceptanceTickboxText: '',
    consentToRenewalMarketingTickboxText: ''
  },
  directDebitPage: {
    guaranteePayee: '',
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: ''
  },
  singlePaymentPage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    subscriptionInformation: '',
    justSoYouKnow: ''
  },
  confirmationPage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    summaryDescription: '',
    emailConfirmation: '',
    backgroundImageUrl: '',
    backgroundImageAltText: '',
    force24Script: ''
  },
  common: {
    navLogoUrl: '',
    faviconUrl: '',
    hideCustomerPlan: false
  },
  footer: {
    contactDetailItems: {
      title: '',
      items: []
    },
    legalDocumentsItems: {
      title: '',
      items: []
    },
    tradingNameItems: {
      title: '',
      items: []
    }
  },
  reasonsToProtect: {
    title: '',
    items: []
  },
  reasonsToExtend: {
    title: '',
    items: []
  },
  usefulDocuments: [],
  quotePage: {
    title: '',
    description: '',
    information: '',
    warning: '',
    justSoYouKnow: '',
    selectionText: '',
    noQuotesErrorMessage: '',
    productTypeCustomisations: []
  },
  failedRegistration: {
    phone: ''
  },
  suppressionPage: { title: '', infoBoxText: '' },
  force24Script: '',
  allureScript: '',
  clarityScript: '',
  croSource: '',
  hideLateRenewalPage: false,
  lateRenewalAllowedDays: 30,
  loaded: false
};

/**
 * Content slice containing reducers for storing CMS content returned by the backend.
 */
export const contentSlice = createSlice({
  name: 'content',
  initialState: initialSiteState,
  reducers: {
    setContent: (s, action: PayloadAction<SliceState>) => {
      Object.assign(s, action.payload);
      s.loaded = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getSuppressionPageDetails.fulfilled, (s, action) => {
      s.common.faviconUrl = action.payload.content?.header?.faviconUrl ?? '';
      s.common.navLogoUrl = action.payload.content?.header?.navLogoUrl ?? '';
      s.footer = action.payload.content?.footer ?? initialSiteState.footer;
      s.suppressionPage =
        action.payload.content?.suppressionPage ??
        initialSiteState.suppressionPage;
      s.loaded = true;
    });
  }
});

export const { setContent } = contentSlice.actions;

export default contentSlice.reducer;
